import { watch, type UnwrapNestedRefs } from 'vue';
import { isArray, isObject } from 'lodash-es';

const LEGACY_RAW_QUERY_PARAM = 'query';
const TERM_FILTER_KEY = 'term';
const LATE_BINDABLE_QUERY_KEYS = new Set(['page', 'pageSize']);

function customStringify(valueToStringify: any) {
  return isArray(valueToStringify) || isObject(valueToStringify) ? JSON.stringify(valueToStringify) : `${valueToStringify}`;
}

function customParse(stringToParse: string) {
  try {
    return JSON.parse(stringToParse);
  } catch (_e) {
    return JSON.parse(`"${stringToParse}"`);
  }
}

function shouldIncludeInQuery(value: any) {
  if (typeof value === 'boolean' || typeof value === 'number') {
    return true;
  }
  if (isArray(value)) {
    return value.length > 0;
  }
  if (isObject(value)) {
    return Object.keys(value).length > 0;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return false;
}

function resolveRouteQueryKey(key: string, objToBind: Record<string, any>, currentUrl: URL) {
  const shouldUseLegacyRawQuery =
    key === TERM_FILTER_KEY &&
    !Object.hasOwn(objToBind, LEGACY_RAW_QUERY_PARAM) &&
    !currentUrl.searchParams.has(TERM_FILTER_KEY) &&
    currentUrl.searchParams.has(LEGACY_RAW_QUERY_PARAM);

  return shouldUseLegacyRawQuery ? LEGACY_RAW_QUERY_PARAM : key;
}

// Experimental
/**
 * A Vue composable that binds an object to route query parameters.
 *
 * @function
 * @name useHBindQueryParams
 * @template T
 * @param {UnwrapNestedRefs<T>} objToBind - The object to bind to the route query parameters.
 * @param {Function} [onRouteQueryUpdate] - Optional callback function to be called when the route query is updated.
 * @returns {Record<string, Ref<string | null>>} - An object containing reactive references to the route query parameters.
 *
 * @description
 * This composable binds the properties of an object to the route query parameters, allowing for reactive updates.
 * It initializes the object properties from the route query parameters and watches for changes to update the route query accordingly.
 * Optionally, a callback function can be provided to be executed whenever the route query is updated.
 */
export const useHBindQueryParams = <T extends Record<string, any>>(objToBind: UnwrapNestedRefs<T>, onRouteQueryUpdate?: () => void) => {
  const routeQueries: Record<string, string | null> = {};
  const routeQueryKeys: Record<string, string> = {};
  const currentUrl = new URL(window.location.href);

  // Implications when putting object keys into URL bar
  // Take into account concepts of validation for our `objToBind` object
  Object.entries(objToBind).forEach(([key, value]) => {
    const shouldInclude = shouldIncludeInQuery(value);

    const initialValue = shouldInclude ? customStringify(value) : null;
    const routeQueryKey = resolveRouteQueryKey(key, objToBind, currentUrl);
    const routeQueryValue = currentUrl.searchParams.get(routeQueryKey);

    routeQueryKeys[key] = routeQueryKey;
    routeQueries[key] = routeQueryValue ? routeQueryValue : initialValue;
  });

  // Initial values for objToBind from route query
  // Note: not ideal to modify a param
  Object.assign(
    objToBind,
    Object.fromEntries(Object.entries(routeQueries).map(([key, value]) => [key, value !== null ? customParse(value) : null])),
  );

  watch(objToBind, () => {
    Object.entries(objToBind).forEach(([key, value]) => {
      if (routeQueries[key] === undefined) {
        if (!LATE_BINDABLE_QUERY_KEYS.has(key)) {
          throw new Error(
            `Key "${key}" not found in routeQueries. ` +
              `This usually means the key was added to the object after initialization. ` +
              `Available keys: [${Object.keys(routeQueries).join(', ')}]`,
          );
        }

        const currentUrl = new URL(window.location.href);
        routeQueryKeys[key] = resolveRouteQueryKey(key, objToBind, currentUrl);
        routeQueries[key] = null;
      }

      const shouldInclude = shouldIncludeInQuery(value);

      const newValue = shouldInclude ? customStringify(value) : null;

      if (routeQueries[key] !== newValue) {
        routeQueries[key] = newValue;
        const url = new URL(window.location.href);
        const routeQueryKey = routeQueryKeys[key];

        if (newValue === null) {
          url.searchParams.delete(routeQueryKey);
        } else if (newValue !== url.searchParams.get(routeQueryKey)) {
          url.searchParams.set(routeQueryKey, newValue);
        }

        window.history.replaceState({}, '', url);
      }
    });

    // Run callback function from params
    if (onRouteQueryUpdate) onRouteQueryUpdate();
  });

  return routeQueries;
};
